import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Router as RouterIcon } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { NAS_TYPES } from './radiusUtils';

const EMPTY = { nasname: '', shortname: '', type: 'mikrotik', secret: '', ports: '', description: '' };

export default function RadiusNas({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delNas, setDelNas] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/radius/nas'); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged && onChanged(); };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ nasname: it.nasname, shortname: it.shortname, type: it.type || 'other', secret: '', ports: it.ports || '', description: it.description || '' }); setOpen(true); };

  const showReloadResult = (data, okMsg) => {
    const warning = data?.warning;
    const reload = data?.reload;
    if (warning) {
      toast.warning(okMsg + ' — perlu perhatian', { description: warning, duration: 9000 });
    } else if (reload?.ok) {
      toast.success(okMsg, { description: 'FreeRADIUS berhasil di-reload — router baru langsung aktif.' });
    } else {
      toast.success(okMsg);
    }
  };

  const save = async () => {
    if (!form.nasname || !form.shortname) return toast.error('NAS Name (IP) & Short Name wajib diisi');
    if (!editing && !form.secret) return toast.error('Shared Secret wajib diisi');
    setSaving(true);
    try {
      const payload = { nasname: form.nasname, shortname: form.shortname, type: form.type, description: form.description, ports: form.ports ? Number(form.ports) : null };
      if (form.secret) payload.secret = form.secret;
      let res;
      if (editing) { res = await api.put(`/radius/nas/${editing.id}`, payload); showReloadResult(res.data, 'NAS diperbarui'); }
      else { res = await api.post('/radius/nas', payload); showReloadResult(res.data, 'NAS ditambahkan'); }
      setOpen(false); refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { const res = await api.delete(`/radius/nas/${delNas.id}`); showReloadResult(res.data, 'NAS dihapus'); setDelNas(null); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Daftar client RADIUS (MikroTik/NAS) dari tabel <span className="font-mono">nas</span>. Shared Secret dimasking & tidak pernah dikirim ke browser.</p>
        <Button onClick={openAdd} data-testid="radius-nas-add"><Plus className="w-4 h-4 mr-1.5" /> Add NAS</Button>
      </div>
      <Card className="border-border"><CardContent className="p-3">
        <div className="border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-muted/40 hover:bg-muted/40">{['NAS Name','Short Name','Type','Ports','Secret','Description','Action'].map((h) => <TableHead key={h} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Memuat…</TableCell></TableRow>)}
              {!loading && items.length === 0 && (<TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-muted-foreground">Belum ada NAS. Klik “Add NAS”.</TableCell></TableRow>)}
              {!loading && items.map((it) => (
                <TableRow key={it.id} data-testid={`radius-nas-row-${it.id}`}>
                  <TableCell className="text-sm font-medium font-mono">{it.nasname}</TableCell>
                  <TableCell className="text-xs">{it.shortname}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{it.type}</Badge></TableCell>
                  <TableCell className="text-xs tabular-nums">{it.ports || '—'}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">••••••••</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.description || '—'}</TableCell>
                  <TableCell><div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => openEdit(it)} data-testid={`radius-nas-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 text-rose-500" onClick={() => setDelNas(it)} data-testid={`radius-nas-del-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="radius-nas-form">
          <SheetHeader><SheetTitle className="flex items-center gap-2"><RouterIcon className="w-4 h-4" /> {editing ? 'Edit NAS' : 'Add NAS'}</SheetTitle><SheetDescription>Shared Secret dienkripsi di server. Kosongkan saat edit bila tidak ingin mengubah.</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs">NAS Name (IP Address)</Label><div className="mt-1"><Input value={form.nasname} onChange={(e) => setForm({ ...form, nasname: e.target.value })} placeholder="10.10.0.1" data-testid="radius-nas-f-name" /></div></div>
            <div><Label className="text-xs">Short Name</Label><div className="mt-1"><Input value={form.shortname} onChange={(e) => setForm({ ...form, shortname: e.target.value })} placeholder="MikroTik-Core" data-testid="radius-nas-f-short" /></div></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">NAS Type</Label><div className="mt-1">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="radius-nas-f-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{NAS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div></div>
              <div><Label className="text-xs">Ports</Label><div className="mt-1"><Input type="number" value={form.ports} onChange={(e) => setForm({ ...form, ports: e.target.value })} placeholder="1812" data-testid="radius-nas-f-ports" /></div></div>
            </div>
            <div><Label className="text-xs">{editing ? 'Shared Secret (kosongkan = tetap)' : 'Shared Secret'}</Label><div className="mt-1"><Input type="password" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} data-testid="radius-nas-f-secret" /></div></div>
            <div><Label className="text-xs">Description</Label><div className="mt-1"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Core Router" data-testid="radius-nas-f-desc" /></div></div>
          </div>
          <SheetFooter className="mt-4"><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="radius-nas-save">{saving ? 'Menyimpan…' : 'Simpan'}</Button></SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delNas} onOpenChange={(o) => !o && setDelNas(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus NAS?</AlertDialogTitle><AlertDialogDescription><span className="font-mono">{delNas?.nasname}</span> ({delNas?.shortname}) akan dihapus dari tabel nas.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="radius-nas-del-confirm">Ya, Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
